import React, { useEffect, useState, useMemo } from 'react';
import ResizableTable from './ResizableTable';
import { extractDocumentIds, processDocumentReferences, processImageReferences, DocumentInfo as DocumentReferenceInfo } from '../../../utils/document-resolver';
import InlineImage from '../../atoms/InlineImage';
import { getDocumentsInfo, generateDocumentUrl, DocumentInfo } from '../thoughtRenderer/toolResults/documentHelpers';
import { DocumentBadge } from './DocumentBadge';

interface MarkdownMessageProps {
  content: string;
  fontSize?: string;
  textColor?: string;
  /** Reserved for streaming parity with web chat (optional visual tweaks later). */
  isStreaming?: boolean;
  onViewDocument?: (url: string, name: string) => void;
}

export const MarkdownMessage: React.FC<MarkdownMessageProps> = ({ 
  content, 
  fontSize = 'text-[13px]',
  textColor = 'text-gray-800',
  isStreaming: _isStreaming = false,
  onViewDocument
}) => {
  const [documents, setDocuments] = useState<DocumentReferenceInfo[]>([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);

  // Extraer IDs de documentos del contenido
  const documentIds = useMemo(() => {
    return extractDocumentIds(content);
  }, [content]);

  // Cargar información de documentos
  useEffect(() => {
    const loadDocuments = async () => {
      if (documentIds.length === 0) {
        setDocuments([]);
        return;
      }

      setIsLoadingDocuments(true);
      try {
        const docs = await getDocumentsInfo(documentIds);
        
        // Generar URLs para documentos que no las tengan
        const docsWithUrls: DocumentReferenceInfo[] = await Promise.all(
          docs.map(async (doc) => {
            let finalUrl = doc.pdf_url;
            
            // Si tenemos source_path pero no pdf_url, generar la URL
            if (!finalUrl && doc.source_path) {
              finalUrl = await generateDocumentUrl(doc.source_path);
            }

            return {
              id: doc.id,
              name: doc.name,
              type: doc.document_type,
              url: finalUrl || undefined
            };
          })
        );

        setDocuments(docsWithUrls);
      } catch (error) {
        console.error('Error loading documents:', error);
        setDocuments([]);
      } finally {
        setIsLoadingDocuments(false);
      }
    };

    loadDocuments();
  }, [documentIds.join(',')]);
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    
    // Tablas - Usar componente ResizableTable
    if (line.trim().startsWith('|')) {
      const tableRows: string[] = [];
      const tableStartIndex = i;
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        tableRows.push(lines[i]);
        i++;
      }
      
      // Validar que hay al menos header y separador
      if (tableRows.length >= 2) {
        const headerRow = tableRows[0];
        const separatorRow = tableRows[1];
        const dataRows = tableRows.slice(2); // Saltar la línea de separación (---|---|---)
        
        elements.push(
          <ResizableTable
            key={`table-${tableStartIndex}`}
            headerRow={headerRow}
            dataRows={dataRows}
          />
        );
      }
      continue;
    }
    
    // Bloques de código (```)
    if (line.trim().startsWith('```')) {
      const codeBlockLines: string[] = [];
      const language = line.trim().replace(/^```/, ''); // Extraer lenguaje si existe
      const codeStartIndex = i;
      i++; // Saltar línea inicial
      
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeBlockLines.push(lines[i]);
        i++;
      }
      
      if (i < lines.length) i++; // Saltar línea de cierre
      
      elements.push(
        <pre key={`code-block-${codeStartIndex}`} className="bg-gray-100 p-3 rounded-md overflow-auto my-2 text-xs font-mono break-words">
          <code className="text-gray-800">{codeBlockLines.join('\n')}</code>
        </pre>
      );
      continue;
    }
    
    // Separadores horizontales (---, ***, ___)
    if (/^[-*_]{3,}$/.test(line.trim())) {
      elements.push(
        <hr key={`hr-${i}`} className="my-4 border-t border-gray-300" />
      );
      i++;
      continue;
    }
    
    // Encabezados H1 (#)
    if (line.trim().startsWith('# ') && !line.trim().startsWith('##')) {
      elements.push(
        <h1 key={`h1-${i}`} className={`text-lg font-semibold text-gray-900 mt-6 mb-3 ${textColor} break-words`}>
          {line.replace(/^#\s+/, '').trim()}
        </h1>
      );
      i++;
      continue;
    }
    
    // Encabezados H2 (##)
    if (line.trim().startsWith('## ') && !line.trim().startsWith('###')) {
      elements.push(
        <h2 key={`h2-${i}`} className={`text-base font-semibold text-gray-900 mt-5 mb-2.5 ${textColor} break-words`}>
          {line.replace(/^##\s+/, '').trim()}
        </h2>
      );
      i++;
      continue;
    }
    
    // Encabezados H3 (###)
    if (line.trim().startsWith('###')) {
      elements.push(
        <h3 key={`h3-${i}`} className={`text-sm font-semibold text-gray-900 mt-6 mb-3 flex items-center gap-2 ${textColor} break-words`}>
          {line.replace(/^###\s+/, '').trim()}
        </h3>
      );
      i++;
      continue;
    }
    
    // Blockquotes (>)
    if (line.trim().startsWith('> ')) {
      const quoteContent = line.replace(/^>\s+/, '');
      elements.push(
        <blockquote key={`blockquote-${i}`} className="border-l-4 border-gray-300 pl-3 my-2 italic text-gray-600 break-words">
          {processInlineMarkdown(quoteContent, documents, onViewDocument)}
        </blockquote>
      );
      i++;
      continue;
    }
    
    // Checkboxes / Task lists (- [ ] o - [x])
    const checkboxMatch = line.match(/^(\s*)([-*])\s+\[([ xX])\]\s+(.+)$/);
    if (checkboxMatch) {
      const indent = checkboxMatch[1].length;
      const isChecked = checkboxMatch[3].toLowerCase() === 'x';
      const taskContent = checkboxMatch[4];
      const units = Math.min(Math.floor(indent / 2), 12); // unidad de 4px, cap a 12
      const spacing = ['ml-0','ml-1','ml-2','ml-3','ml-4','ml-5','ml-6','ml-7','ml-8','ml-9','ml-10','ml-11','ml-12'];
      const marginClass = spacing[units] || 'ml-0';
      
      elements.push(
        <div key={`checkbox-${i}`} className={`flex items-start gap-2 mb-1 ${textColor} break-words ${marginClass}`}>
          <input 
            type="checkbox" 
            checked={isChecked} 
            readOnly 
            aria-label={`Tarea: ${taskContent}`}
            title={taskContent}
            className="mt-1 cursor-default"
          />
          <span className={`flex-1 ${fontSize} ${isChecked ? 'line-through text-gray-500' : ''}`}>
            {processInlineMarkdown(taskContent, documents, onViewDocument)}
          </span>
        </div>
      );
      i++;
      continue;
    }
    
    // Listas (* o -) con soporte para indentación
    const listMatch = line.match(/^(\s*)([-*])\s+(.+)$/);
    if (listMatch) {
      const indent = listMatch[1].length;
      const listContent = listMatch[3];
      const units = Math.min(Math.floor(indent / 2), 12);
      const spacing = ['ml-0','ml-1','ml-2','ml-3','ml-4','ml-5','ml-6','ml-7','ml-8','ml-9','ml-10','ml-11','ml-12'];
      const marginClass = spacing[Math.min(units + 4, 12)];
      
      elements.push(
        <li key={`li-${i}`} className={`pl-2 list-disc marker:text-gray-300 ${textColor} mb-1 ${fontSize} break-words ${marginClass}`}>
          {processInlineMarkdown(listContent, documents, onViewDocument)}
        </li>
      );
      i++;
      continue;
    }

    // Listas Numeradas (1.) con soporte para indentación
    const numberedListMatch = line.match(/^(\s*)(\d+)\.\s+(.+)$/);
    if (numberedListMatch) {
      const indent = numberedListMatch[1].length;
      const number = numberedListMatch[2];
      const listContent = numberedListMatch[3];
      const units = Math.min(Math.floor(indent / 2), 12);
      const spacing = ['ml-0','ml-1','ml-2','ml-3','ml-4','ml-5','ml-6','ml-7','ml-8','ml-9','ml-10','ml-11','ml-12'];
      const marginClass = spacing[Math.min(units + 1, 12)];
      
      elements.push(
        <div key={`num-${i}`} className={`flex gap-2 mb-2 ${textColor} break-words ${marginClass}`}>
          <span className="font-mono text-xs text-gray-400 mt-0.5">{number}.</span>
          <span className={`flex-1 ${fontSize}`}>
            {processInlineMarkdown(listContent, documents, onViewDocument)}
          </span>
        </div>
      );
      i++;
      continue;
    }
    
    // Párrafos normales
    if (line.trim()) {
      elements.push(
        <p key={`p-${i}`} className={`mb-2 ${fontSize} ${textColor} leading-relaxed break-words`}>
          {processInlineMarkdown(line, documents, onViewDocument)}
        </p>
      );
    } else {
      elements.push(<div key={`space-${i}`} className="h-2" />);
    }
    
    i++;
  }
  
  return <div className={`${fontSize} leading-relaxed`}>{elements}</div>;
};

// Procesar markdown inline (código, enlaces, negritas, cursivas, referencias a documentos)
const processInlineMarkdown = (
  text: string, 
  documents?: Array<{ id: string; name: string; type?: string; url?: string }>,
  onViewDocument?: (url: string, name: string) => void
): React.ReactNode => {
  // Primero procesar imágenes inline en todo el texto
  const imageProcessed = processImageReferences(text, (url, alt, idx) => (
    <InlineImage key={`md-img-${idx}`} src={url} alt={alt} className="max-w-[280px] max-h-[400px] my-2" />
  ));

  // Si hay documentos disponibles, procesar documentos dentro de cada segmento texto
  if (documents && documents.length > 0 && onViewDocument) {
    const partsAfterDocs: React.ReactNode[] = [];

    imageProcessed.forEach((part, partIdx) => {
      if (typeof part === 'string') {
        const processedParts = processDocumentReferences(
          part,
          documents,
          (docId, docName, docType, docUrl, matchIndex, page) => (
            <DocumentBadge
              key={`doc-${docId}-${partIdx}-${matchIndex}`}
              documentId={docId}
              documentName={docName}
              documentType={docType}
              documentUrl={docUrl}
              page={page}
              onViewDocument={onViewDocument}
            />
          )
        );

        processedParts.forEach((p, i) => {
          if (typeof p === 'string') {
            partsAfterDocs.push(<React.Fragment key={`md-text-${partIdx}-${i}`}>{processAllInlineMarkdown(p, partIdx + i)}</React.Fragment>);
          } else {
            partsAfterDocs.push(<React.Fragment key={`md-badge-${partIdx}-${i}`}>{p}</React.Fragment>);
          }
        });
      } else {
        // image node
        partsAfterDocs.push(part);
      }
    });

    return <>{partsAfterDocs}</>;
  }

  // Si no hay documentos, devolver los segmentos con markdown inline procesado
  return (
    <>
      {imageProcessed.map((part, idx) => (
        typeof part === 'string' ? <React.Fragment key={`md-text-only-${idx}`}>{processAllInlineMarkdown(part, idx)}</React.Fragment> : <React.Fragment key={`md-img-only-${idx}`}>{part}</React.Fragment>
      ))}
    </>
  );
};

// Procesar todos los tipos de markdown inline en el orden correcto
const processAllInlineMarkdown = (text: string, baseKey: number = 0): React.ReactNode => {
  // El orden es importante: código primero (para no procesar markdown dentro de código)
  // luego enlaces, luego negrita+cursiva, tachado, negritas, cursivas
  const result: React.ReactNode[] = [];
  let currentText = text;
  let keyCounter = 0;

  // Patrón combinado para capturar todos los elementos inline
  // Orden: código inline (`code`), enlaces ([text](url)), negrita+cursiva (***text***), tachado (~~text~~), negritas (**text** o __text__), cursivas (*text* o _text_)
  const pattern = /(`[^`]+`|\[([^\]]+)\]\(([^)]+)\)|\*\*\*([^*]+)\*\*\*|~~([^~]+)~~|\*\*([^*]+)\*\*|__([^_]+)__|(?<!\*)\*([^*]+)\*(?!\*)|(?<!_)_([^_]+)_(?!_))/g;
  
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    // Añadir texto antes del match
    if (match.index > lastIndex) {
      result.push(
        <span key={`text-${baseKey}-${keyCounter++}`}>
          {text.substring(lastIndex, match.index)}
        </span>
      );
    }

    const fullMatch = match[0];

    // Código inline (`code`)
    if (fullMatch.startsWith('`') && fullMatch.endsWith('`')) {
      result.push(
        <code key={`code-${baseKey}-${keyCounter++}`} className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono text-gray-800">
          {fullMatch.slice(1, -1)}
        </code>
      );
    }
    // Enlaces ([text](url))
    else if (fullMatch.startsWith('[') && match[2] && match[3]) {
      const linkText = match[2];
      const linkUrl = match[3];
      result.push(
        <a 
          key={`link-${baseKey}-${keyCounter++}`}
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-emerald-600 underline underline-offset-2 hover:text-emerald-700 cursor-pointer"
        >
          {linkText}
        </a>
      );
    }
    // Negrita + Cursiva (***text***)
    else if (fullMatch.startsWith('***') && fullMatch.endsWith('***') && match[4]) {
      result.push(
        <strong key={`bold-italic-${baseKey}-${keyCounter++}`} className="font-semibold italic text-gray-900">
          {match[4]}
        </strong>
      );
    }
    // Tachado (~~text~~)
    else if (fullMatch.startsWith('~~') && fullMatch.endsWith('~~') && match[5]) {
      result.push(
        <del key={`strikethrough-${baseKey}-${keyCounter++}`} className="line-through text-gray-600">
          {match[5]}
        </del>
      );
    }
    // Negritas (**text**)
    else if (fullMatch.startsWith('**') && fullMatch.endsWith('**') && match[6]) {
      result.push(
        <strong key={`bold-${baseKey}-${keyCounter++}`} className="font-semibold text-gray-900">
          {match[6]}
        </strong>
      );
    }
    // Negritas (__text__)
    else if (fullMatch.startsWith('__') && fullMatch.endsWith('__') && match[7]) {
      result.push(
        <strong key={`bold-${baseKey}-${keyCounter++}`} className="font-semibold text-gray-900">
          {match[7]}
        </strong>
      );
    }
    // Cursivas (*text*)
    else if (fullMatch.startsWith('*') && fullMatch.endsWith('*') && !fullMatch.startsWith('**') && match[8]) {
      result.push(
        <em key={`italic-${baseKey}-${keyCounter++}`} className="italic text-gray-800">
          {match[8]}
        </em>
      );
    }
    // Cursivas (_text_)
    else if (fullMatch.startsWith('_') && fullMatch.endsWith('_') && !fullMatch.startsWith('__') && match[9]) {
      result.push(
        <em key={`italic-${baseKey}-${keyCounter++}`} className="italic text-gray-800">
          {match[9]}
        </em>
      );
    }

    lastIndex = match.index + fullMatch.length;
  }

  // Añadir texto restante
  if (lastIndex < text.length) {
    result.push(
      <span key={`text-${baseKey}-${keyCounter++}`}>
        {text.substring(lastIndex)}
      </span>
    );
  }

  return result.length > 0 ? <>{result}</> : text;
};

export default MarkdownMessage;

